import { getLogger } from './logger';
import type { WidgetState, WidgetTranslations } from './types';
import { createAudioVisualizer, stopAudioVisualizer } from './audio-visualizer';
import { getRemoteAudioTrack, getLocalAudioTrack } from './livekit';

const logger = getLogger('UI');

/**
 * SVG icons for different states (for backward compatibility)
 */
function createStateIcon(state: WidgetState): string {
  switch (state) {
    case 'connecting':
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="6" stroke="#2196F3" stroke-width="2" stroke-dasharray="12.566 4.188" stroke-dashoffset="0">
          <animateTransform attributeName="transform" type="rotate" from="0 8 8" to="360 8 8" dur="1s" repeatCount="indefinite"/>
        </circle>
      </svg>`;
    case 'streaming':
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="4" fill="#4CAF50"/>
        <circle cx="8" cy="8" r="6" stroke="#4CAF50" stroke-width="2" opacity="0.5">
          <animate attributeName="r" values="6;8;6" dur="2s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.5;0;0.5" dur="2s" repeatCount="indefinite"/>
        </circle>
      </svg>`;
    case 'error':
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="6" fill="#FF5252"/>
        <path d="M6 6L10 10M10 6L6 10" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>`;
    case 'ended':
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="6" fill="#4CAF50"/>
        <path d="M5 8L7 10L11 6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    default:
      return '';
  }
}

/**
 * Create connecting state visualization (clean loading indicator)
 */
function createConnectingVisualization(parentGroup: SVGGElement, text: string): void {
  // Add transform to position the visualization (offset for robot illustration, moved right and higher)
  parentGroup.setAttribute('transform', 'translate(580, 340)');

  const loadingGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');

  // Background circle (same style as ended state)
  const loadingCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  loadingCircle.setAttribute('cx', '0');
  loadingCircle.setAttribute('cy', '0');
  loadingCircle.setAttribute('r', '60');
  loadingCircle.setAttribute('fill', '#4CAF50');
  loadingCircle.setAttribute('opacity', '0.15');

  // Create Apple-style spinning dots (12 dots arranged in a circle)
  const numDots = 12;
  const radius = 25;
  const dotRadius = 5;

  for (let i = 0; i < numDots; i++) {
    const angle = (i * 360) / numDots;
    const radians = (angle * Math.PI) / 180;
    const x = Math.cos(radians) * radius;
    const y = Math.sin(radians) * radius;

    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('cx', x.toString());
    dot.setAttribute('cy', y.toString());
    dot.setAttribute('r', dotRadius.toString());
    dot.setAttribute('fill', '#4CAF50');

    // Create opacity animation for each dot with staggered delays
    const animateOpacity = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
    animateOpacity.setAttribute('attributeName', 'opacity');
    animateOpacity.setAttribute('values', '1;0.2;1');
    animateOpacity.setAttribute('dur', '1.2s');
    animateOpacity.setAttribute('begin', `${(i * 1.2) / numDots}s`);
    animateOpacity.setAttribute('repeatCount', 'indefinite');
    dot.appendChild(animateOpacity);

    loadingGroup.appendChild(dot);
  }

  loadingGroup.appendChild(loadingCircle);

  // Status text
  const statusText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  statusText.setAttribute('x', '0');
  statusText.setAttribute('y', '125');
  statusText.setAttribute('text-anchor', 'middle');
  statusText.setAttribute(
    'font-family',
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif'
  );
  statusText.setAttribute('font-size', '36');
  statusText.setAttribute('font-weight', '400');
  statusText.setAttribute('fill', '#333333');
  statusText.setAttribute('opacity', '1');
  statusText.textContent = text;

  parentGroup.appendChild(loadingGroup);
  parentGroup.appendChild(statusText);
}

/**
 * Create error state visualization (clean error icon)
 */
function createErrorVisualization(parentGroup: SVGGElement, text: string): void {
  // Add transform to position the visualization (offset for robot illustration, moved right and higher)
  parentGroup.setAttribute('transform', 'translate(580, 340)');

  const errorGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');

  // Clean error icon - simple X in circle
  const errorCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  errorCircle.setAttribute('cx', '0');
  errorCircle.setAttribute('cy', '0');
  errorCircle.setAttribute('r', '60');
  errorCircle.setAttribute('fill', '#FF5252');
  errorCircle.setAttribute('opacity', '0.15');

  // X icon
  const xLine1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  xLine1.setAttribute('x1', '-24');
  xLine1.setAttribute('y1', '-24');
  xLine1.setAttribute('x2', '24');
  xLine1.setAttribute('y2', '24');
  xLine1.setAttribute('stroke', '#FF5252');
  xLine1.setAttribute('stroke-width', '8');
  xLine1.setAttribute('stroke-linecap', 'round');

  const xLine2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  xLine2.setAttribute('x1', '24');
  xLine2.setAttribute('y1', '-24');
  xLine2.setAttribute('x2', '-24');
  xLine2.setAttribute('y2', '24');
  xLine2.setAttribute('stroke', '#FF5252');
  xLine2.setAttribute('stroke-width', '8');
  xLine2.setAttribute('stroke-linecap', 'round');

  errorGroup.appendChild(errorCircle);
  errorGroup.appendChild(xLine1);
  errorGroup.appendChild(xLine2);

  // Error text with wrapping support using foreignObject
  const errorTextFo = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
  errorTextFo.setAttribute('x', '-450');
  errorTextFo.setAttribute('y', '100');
  errorTextFo.setAttribute('width', '900');
  errorTextFo.setAttribute('height', '200');

  const errorTextDiv = document.createElement('div');
  errorTextDiv.style.cssText = `
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif;
    font-size: 36px;
    font-weight: 400;
    color: #FF5252;
    text-align: center;
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 1.2;
  `;
  errorTextDiv.textContent = text;

  errorTextFo.appendChild(errorTextDiv);

  parentGroup.appendChild(errorGroup);
  parentGroup.appendChild(errorTextFo);
}

/**
 * Create ended state visualization (clean success indicator)
 */
function createEndedVisualization(parentGroup: SVGGElement, text: string): void {
  // Add transform to position the visualization (offset for robot illustration, moved right and higher)
  parentGroup.setAttribute('transform', 'translate(580, 340)');

  const successGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');

  // Clean success indicator - checkmark in circle
  const successCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  successCircle.setAttribute('cx', '0');
  successCircle.setAttribute('cy', '0');
  successCircle.setAttribute('r', '60');
  successCircle.setAttribute('fill', '#4CAF50');
  successCircle.setAttribute('opacity', '0.15');

  // Clean checkmark
  const checkmarkPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  checkmarkPath.setAttribute('d', 'M -20 0 L -6 15 L 20 -10');
  checkmarkPath.setAttribute('stroke', '#4CAF50');
  checkmarkPath.setAttribute('stroke-width', '8');
  checkmarkPath.setAttribute('stroke-linecap', 'round');
  checkmarkPath.setAttribute('stroke-linejoin', 'round');
  checkmarkPath.setAttribute('fill', 'none');

  successGroup.appendChild(successCircle);
  successGroup.appendChild(checkmarkPath);

  // Success text
  const successText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  successText.setAttribute('x', '0');
  successText.setAttribute('y', '125');
  successText.setAttribute('text-anchor', 'middle');
  successText.setAttribute(
    'font-family',
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif'
  );
  successText.setAttribute('font-size', '36');
  successText.setAttribute('font-weight', '400');
  successText.setAttribute('fill', '#333333');
  successText.setAttribute('opacity', '1');
  successText.textContent = text;

  parentGroup.appendChild(successGroup);
  parentGroup.appendChild(successText);
}

/**
 * Remove existing state visualization from SVG
 */
export function removeStateVisualization(svgElement: Element): void {
  // Remove traditional state visualization
  const existingVisualization = svgElement.querySelector('#state-visualization');
  if (existingVisualization) {
    existingVisualization.remove();
  }

  // Stop and remove audio visualizer if it exists
  const audioVisualizerElement = svgElement.querySelector('#audio-visualizer');
  if (audioVisualizerElement) {
    stopAudioVisualizer();
  }
}

/**
 * Add state visualization to SVG element
 */
export function addStateVisualization(
  svgElement: Element,
  state: WidgetState,
  translations?: WidgetTranslations
): void {
  // Remove existing visualization
  removeStateVisualization(svgElement);

  // For streaming state, use dynamic audio visualizer
  if (state === 'streaming') {
    const remoteTrack = getRemoteAudioTrack();
    const localTrack = getLocalAudioTrack();
    createAudioVisualizer(
      svgElement,
      translations?.streaming || 'Streaming...',
      remoteTrack ?? undefined,
      localTrack ?? undefined
    );
    return;
  }

  // Create new visualization for other states using proper SVG namespace
  const visualizationGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  visualizationGroup.setAttribute('id', 'state-visualization');
  visualizationGroup.setAttribute('class', 'state-visualization');

  logger.debug('Creating state visualization', { state });

  try {
    switch (state) {
      case 'connecting':
        createConnectingVisualization(
          visualizationGroup,
          translations?.connecting || 'Connecting...'
        );
        break;
      case 'error':
        createErrorVisualization(visualizationGroup, translations?.error || 'Error');
        break;
      case 'ended':
        createEndedVisualization(visualizationGroup, translations?.ended || 'Conversation ended');
        break;
      default:
        logger.warn('Unknown state for visualization', { state });
        return;
    }

    svgElement.appendChild(visualizationGroup);
    logger.debug('State visualization added successfully', { state });
  } catch (error) {
    logger.error('Error creating state visualization', { state, error });
  }
}

// Export the state icon function for use in ui.ts
export { createStateIcon };
